import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/heroes';
import RatingBadge from '@/components/base/RatingBadge';

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = heroSlides.length;

  const goTo = useCallback((index: number) => setCurrent((index + count) % count), [count]);
  const next = useCallback(() => setCurrent((prev) => (prev + 1) % count), [count]);
  const prev = useCallback(() => setCurrent((prev) => (prev - 1 + count) % count), [count]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const handleTouchStart = (event: ReactTouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };
  const handleTouchEnd = (event: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = event.changedTouches[0].clientX - touchStartX.current;
    if (diff > 50) prev();
    else if (diff < -50) next();
    touchStartX.current = null;
  };

  return (
    <section className="w-full px-4 md:px-8 pt-5 md:pt-6">
      <div
        className="relative w-full h-[380px] md:h-[560px] rounded-xl overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <img src={slide.image} alt={`${slide.title}剧照`} title={`${slide.title} 在线观看`} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/10"></div>

            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-6 md:px-14 max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 text-primary-950 text-xs md:text-sm font-semibold px-3 py-1 mb-3 md:mb-4">
                  <i className="ri-fire-fill"></i>热门推荐
                </span>
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-5xl leading-tight mb-3">
                  {slide.title}
                </h2>
                <p className="text-background-100 text-sm md:text-base mb-4 md:mb-6">{slide.tagline}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${slide.id}`}
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line text-lg"></i>查看详情
                  </Link>
                  <span className="inline-flex items-center">
                    <RatingBadge rating={slide.rating} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex items-center gap-2">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到 ${slide.title}`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${index === current ? 'w-7 bg-accent-500' : 'w-2 bg-background-50/60'}`}
            ></button>
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="absolute top-1/2 -translate-y-1/2 left-3 md:left-5 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/25 hover:bg-background-50/45 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="absolute top-1/2 -translate-y-1/2 right-3 md:right-5 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/25 hover:bg-background-50/45 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}